import React from "react";
import { Box, Typography, Dialog } from "@mui/material";
import { C } from "../constants";
import { IPD_COLORS, ipdSemanticSx } from "../../ipd/utils/ipdColors";
import SvgIcon from "../SvgIcon";
import IpdButton from "../../ipd/utils/IpdButton";

// Presentational primitives shared by the three Cash Counter screens.
//
// `cardStyle`/`Card`, `labelStyle`, `inputStyle`, and `Btn`/`VARIANTS` were
// retired in the Phase B design-system migration (see
// docs/superpowers/specs/2026-07-17-ipd-billing-design-system-migration-design.md
// § "CashCounterUI.js — partial retirement"). Consumers now import
// `IpdSectionCard`, `IpdTextField`/`IpdFormField`, and `IpdButton` directly
// from src/pages/ipd/utils. What remains here are billing-specific
// compositions (stat tiles, session pills, error bar, mode-totals grid, a
// modal shell) with no equivalent in ipd/utils.

export function StatTile({ label, value, sub, tone }) {
  return (
    <Box sx={{ flex: "1 1 150px", backgroundColor: IPD_COLORS.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "14px 16px" }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>{label}</Typography>
      <Typography sx={{ fontSize: "20px", fontWeight: 800, color: tone || C.text }}>{value}</Typography>
      {sub != null && <Typography sx={{ fontSize: "11px", color: C.textSub, marginTop: "3px", fontWeight: 600 }}>{sub}</Typography>}
    </Box>
  );
}

/** Session status pill — OPEN / CLOSED / No session. */
export function SessionChip({ status }) {
  const s = String(status || "").toUpperCase();
  // CLOSED has no ipdSemanticSx equivalent (neutral, not warning/error) — kept literal.
  const openSx = ipdSemanticSx("success");
  const noSessionSx = ipdSemanticSx("warning");
  const tone =
    s === "OPEN" ? { background: openSx.bgcolor, color: openSx.color }
    : s === "CLOSED" ? { background: IPD_COLORS.pageBg, color: C.textSub }
    : { background: noSessionSx.bgcolor, color: noSessionSx.color };
  return (
    <Box component="span" sx={{ fontSize: "12px", fontWeight: 700, padding: "4px 12px", borderRadius: "100px", ...tone }}>
      {s || "No session"}
    </Box>
  );
}

/** Inline error with an optional retry affordance. */
export function ErrorBar({ message, onRetry, busy }) {
  if (!message) return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", backgroundColor: IPD_COLORS.error.bg, border: `1px solid ${IPD_COLORS.error.border}`, borderRadius: "8px", padding: "10px 14px" }}>
      <Box component="span" sx={{ fontSize: "12px", color: IPD_COLORS.error.text, fontWeight: 600 }}>{message}</Box>
      {onRetry && (
        <IpdButton
          variant="outlined"
          size="small"
          onClick={onRetry}
          disabled={busy}
          startIcon={<SvgIcon name="refresh" size={13} color={IPD_COLORS.error.text} />}
          sx={{
            color: IPD_COLORS.error.text,
            borderColor: IPD_COLORS.error.border,
            backgroundColor: IPD_COLORS.cardBg,
            "&:hover": { borderColor: IPD_COLORS.error.text, backgroundColor: IPD_COLORS.error.bg },
          }}
        >
          Retry
        </IpdButton>
      )}
    </Box>
  );
}

/** Totals-by-payment-mode table (shared by dashboard + interim + final review). */
export function ModeTotals({ modes, fmt }) {
  return (
    <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
      {modes.map((m) => (
        <Box key={m.key} sx={{ flex: "1 1 100px", border: `1px solid ${C.border}`, borderRadius: "9px", padding: "10px 12px", textAlign: "center" }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.textSub, marginBottom: "4px" }}>{m.label}</Typography>
          <Typography sx={{ fontSize: "15px", fontWeight: 800, color: C.text }}>{fmt(m.amount)}</Typography>
          {m.count > 0 && <Typography sx={{ fontSize: "10px", color: C.textSub, marginTop: "2px" }}>{m.count} txn</Typography>}
        </Box>
      ))}
    </Box>
  );
}

export function ModalShell({ title, subtitle, onClose, children, footer, width = 440 }) {
  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: "16px", padding: "28px", width: `${width}px`, maxWidth: "92vw", maxHeight: "90vh", overflowY: "auto" } }}
    >
      <Typography component="h3" sx={{ margin: "0 0 6px", fontSize: "17px", fontWeight: 800, color: C.text }}>{title}</Typography>
      {subtitle && <Typography component="p" sx={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>{subtitle}</Typography>}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>{children}</Box>
      <Box sx={{ display: "flex", gap: "10px", marginTop: "22px" }}>{footer}</Box>
    </Dialog>
  );
}

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
};
